import { monthlyTarget } from "../consts/demandplanning";
import {
    categoryPctForName,
    computeSubcategoryYtdForecast,
    subcategoryPctOfChannel,
    sumSubcategoryPctWithinCategory,
} from "./forecastPlanningDataColumn";

const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

function toUnits(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number.parseFloat(value.replace(/,/g, ""));
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

export function stanceForVariance(variancePct, closedMonths) {
    if (closedMonths < 2) return "Hold";
    if (variancePct >= 0.1 && closedMonths >= 3) return "Support";
    if (variancePct <= -0.3 && closedMonths >= 3) return "Trim forecast";
    if (variancePct <= -0.2 && closedMonths >= 3) return "Rebalance needed";
    return "Hold";
}

export function topLineStanceForVariance(variancePct, closedMonths) {
    if (closedMonths < 2) return "Hold";
    if (variancePct >= 0.08 && closedMonths >= 3) return "Support";
    if (variancePct <= -0.08 && closedMonths >= 3) return "Trim forecast";
    if (Math.abs(variancePct) >= 0.04 && closedMonths >= 3) return "Rebalance needed";
    return "Hold";
}

function cappedRecommendation(currentPct, targetPct, variancePct, closedMonths) {
    if (closedMonths < 3) return currentPct;

    const gap = targetPct - currentPct;
    const absVariance = Math.abs(variancePct);
    if (absVariance < 0.1) return currentPct;

    const maxMoveRatio = absVariance >= 0.3 ? 0.25 : 0.1;
    const maxMove = currentPct * maxMoveRatio;

    if (gap > 0) return clamp(currentPct + Math.min(gap, maxMove));
    if (gap < 0) return clamp(currentPct - Math.min(Math.abs(gap), maxMove));
    return currentPct;
}

function governanceSummary(stance, variancePct, closedMonths, scope) {
    if (closedMonths < 2) {
        return `${scope} is still inside the early actuals window. Hold the plan until more closed months are available.`;
    }
    if (closedMonths < 3 && Math.abs(variancePct) >= 0.1) {
        return `${scope} is showing a signal, but the variance has not persisted long enough for a governed rebalance yet.`;
    }
    switch (stance) {
        case "Support":
            return `${scope} is outperforming its YTD forecast, so the forecast can support a higher share if inventory and demand destination checks remain valid.`;
        case "Trim forecast":
            return `${scope} is materially behind its YTD forecast after enough closed months, so a governed trim is eligible if demand can be redirected credibly.`;
        case "Rebalance needed":
            return `${scope} is behind plan beyond the watch band and has persisted long enough to justify a controlled rebalance review.`;
        default:
            return `${scope} is tracking close enough to plan that the variance should be treated as forecast noise for now.`;
    }
}

function buildDestinationLabel(card, receivers, fallbackReceivers) {
    if (card.pctDelta >= -0.0005) {
        if (card.pctDelta > 0.0005) return "Receiving support";
        return "Hold";
    }
    if (receivers.length > 0) return receivers.map((receiver) => receiver.category).join(", ");
    if (fallbackReceivers.length > 0) {
        return `Watch ${fallbackReceivers.map((receiver) => receiver.category).join(", ")}`;
    }
    return "No governed destination yet";
}

function buildDestinationSummary(card, receivers, fallbackReceivers, fmtPercent) {
    if (card.pctDelta >= -0.0005) {
        if (card.pctDelta > 0.0005) {
            return `${card.category} can receive incremental share if weaker categories need to be trimmed.`;
        }
        return `${card.category} does not need a destination callout because the current recommendation is effectively hold.`;
    }
    if (receivers.length > 0) {
        const receiverText = receivers
            .map((receiver) => `${receiver.category} (+${fmtPercent(receiver.shareDelta)})`)
            .join(" and ");
        return `If this share is reduced, reallocate it toward ${receiverText} based on the stronger recent actual mix.`;
    }
    if (fallbackReceivers.length > 0) {
        const fallbackText = fallbackReceivers
            .map((receiver) => `${receiver.category} (+${fmtPercent(receiver.shareDelta)})`)
            .join(" and ");
        return `No category clears the governed support threshold yet. Closest watchlist destinations are ${fallbackText}, so this should stay a reforecast review until one strengthens.`;
    }
    return "No receiving category is strong enough yet, so this should be treated as a reforecast review rather than an immediate rebalance.";
}

export function buildTopLineRecommendation({
    revenueTarget,
    ytdForecast,
    ytdActual,
    closedMonths,
}) {
    const variancePct = ytdForecast > 0 ? (ytdActual - ytdForecast) / ytdForecast : 0;
    const stance = topLineStanceForVariance(variancePct, closedMonths);
    const governedMovePct =
        closedMonths >= 3 && Math.abs(variancePct) >= 0.08
            ? Math.max(Math.min(variancePct * 0.5, 0.08), -0.12)
            : 0;
    const recommendedTarget = Math.round(revenueTarget * (1 + governedMovePct));
    const deltaAmount = recommendedTarget - revenueTarget;
    const deltaPct = revenueTarget > 0 ? deltaAmount / revenueTarget : 0;

    let governanceText =
        "The current top-line forecast is close enough to actual pace that it can remain in place while downstream mix is monitored.";
    if (closedMonths < 2) {
        governanceText =
            "The current top-line forecast is still inside the early actuals window, so it should be held until more months close.";
    } else if (closedMonths < 3 && Math.abs(variancePct) >= 0.08) {
        governanceText =
            "The top-line forecast is showing a signal, but it has not persisted long enough to justify a governed revision yet.";
    } else if (stance === "Trim forecast") {
        governanceText =
            "The business is pacing materially below the current YTD forecast, so a governed reduction to the total revenue target is recommended before any mix shifts are approved.";
    } else if (stance === "Support") {
        governanceText =
            "The business is pacing above the current YTD forecast, so a governed increase to the total revenue target is recommended before any mix shifts are approved.";
    } else if (stance === "Rebalance needed") {
        governanceText =
            "The business is off the current YTD pace enough to justify revisiting the total revenue target before downstream share changes are approved.";
    }

    const stageSummary =
        Math.abs(deltaPct) >= 0.01 && stance !== "Hold"
            ? "Approve the revised top-line forecast first. Once approved, category and subcategory demand-shift recommendations will be staged together underneath it for separate review."
            : "The top-line forecast is already aligned closely enough that category and subcategory demand-shift recommendations can be reviewed now.";

    return {
        recommendationId: `forecast-topline:${Math.round(revenueTarget)}:${Math.round(recommendedTarget)}`,
        currentTarget: revenueTarget,
        recommendedTarget,
        deltaAmount,
        deltaPct,
        ytdForecast,
        ytdActual,
        variancePct,
        stance,
        governanceSummary: governanceText,
        stageSummary,
    };
}

export function buildAiRebalanceRecommendations({
    revenueTarget,
    categories,
    subCategories,
    months,
    closedMonths,
    scopedCategories,
    scopedSubCategories,
    categoryLtmByName,
    subCategoryLtmById,
    tableRecords = [],
    fmtPercent,
}) {
    const ytdForecastTotal = months
        .slice(0, closedMonths)
        .reduce((sum, row) => sum + monthlyTarget(revenueTarget, row), 0);
    const totalYtdActual = categories.reduce((sum, row) => sum + (row.ytd_actuals ?? 0), 0);
    const topLineRecommendation = buildTopLineRecommendation({
        revenueTarget,
        ytdForecast: ytdForecastTotal,
        ytdActual: totalYtdActual,
        closedMonths,
    });

    const totalActual = categories.reduce((sum, row) => sum + (row.ytd_actuals ?? 0), 0) || 1;
    const baseCards = categories
        .filter((row) => scopedCategories.size === 0 || scopedCategories.has(row.category))
        .map((row) => {
            const currentPct = row.pct ?? 0;
            const actualShare = (row.ytd_actuals ?? 0) / totalActual;
            const ltmShare = categoryLtmByName.get(row.category) ?? currentPct;
            const desiredPct = clamp(actualShare * 0.7 + ltmShare * 0.3);
            const ytdForecast = ytdForecastTotal * currentPct;
            const variancePct =
                ytdForecast > 0 ? ((row.ytd_actuals ?? 0) - ytdForecast) / ytdForecast : 0;
            const recommendedPct = cappedRecommendation(
                currentPct,
                desiredPct,
                variancePct,
                closedMonths
            );
            const pctDelta = recommendedPct - currentPct;
            const relatedSubCategories = subCategories.filter((item) => item.category === row.category);
            const topSubCategory =
                relatedSubCategories
                    .slice()
                    .sort((left, right) => (right.ytd_actuals ?? 0) - (left.ytd_actuals ?? 0))[0]
                    ?.sub_category ?? "—";
            const unitsToOrder = tableRecords
                .filter((record) => record.category === row.category)
                .reduce((sum, record) => sum + toUnits(record.unitsToOrder), 0);
            const stance = stanceForVariance(variancePct, closedMonths);

            return {
                recommendationId: `forecast-category:${row.category}`,
                category: row.category,
                currentPct,
                recommendedPct,
                pctDelta,
                actualShare,
                ltmShare,
                variancePct,
                ytdForecast,
                ytdActual: row.ytd_actuals ?? 0,
                unitsToOrder,
                topSubCategory,
                stance,
                governanceSummary: governanceSummary(stance, variancePct, closedMonths, row.category),
            };
        });

    const supportReceivers = baseCards
        .filter((card) => card.pctDelta > 0.0005 && card.stance === "Support")
        .sort((left, right) => right.pctDelta - left.pctDelta);
    const fallbackReceivers = baseCards
        .filter((card) => card.pctDelta > 0.0005)
        .sort((left, right) => right.pctDelta - left.pctDelta);

    const categoryCards = baseCards
        .map((card) => {
            let remainingNeed = Math.max(Math.abs(Math.min(card.pctDelta, 0)), 0);
            const receivers = [];
            if (remainingNeed > 0) {
                supportReceivers.forEach((receiver) => {
                    if (receiver.category === card.category || remainingNeed <= 0) return;
                    const shareDelta = Math.min(receiver.pctDelta, remainingNeed);
                    if (shareDelta <= 0) return;
                    receivers.push({ category: receiver.category, shareDelta });
                    remainingNeed -= shareDelta;
                });
            }
            const fallbackCandidates = fallbackReceivers
                .filter(
                    (receiver) =>
                        receiver.category !== card.category &&
                        !receivers.some((item) => item.category === receiver.category)
                )
                .slice(0, 2)
                .map((receiver) => ({
                    category: receiver.category,
                    shareDelta: receiver.pctDelta,
                }));

            return {
                ...card,
                receivers,
                fallbackReceivers: fallbackCandidates,
                destinationLabel: buildDestinationLabel(card, receivers, fallbackCandidates),
                destinationSummary: buildDestinationSummary(
                    card,
                    receivers,
                    fallbackCandidates,
                    fmtPercent
                ),
            };
        })
        .filter((card) => Math.abs(card.pctDelta) >= 0.0005 && card.stance !== "Hold")
        .sort((left, right) => Math.abs(right.variancePct) - Math.abs(left.variancePct));

    const subCategoryCards = subCategories
        .filter(
            (row) => scopedSubCategories.size === 0 || scopedSubCategories.has(row.sub_category)
        )
        .map((row) => {
            const currentPct = row.pct_of_channel ?? 0;
            const parentCategoryPct = categoryPctForName(categories, row.category);
            const siblings = subCategories.filter((item) => item.category === row.category);
            const siblingActuals = siblings.reduce((sum, item) => sum + (item.ytd_actuals ?? 0), 0);
            const siblingWithinCategoryTotal =
                sumSubcategoryPctWithinCategory(subCategories, row.category) || 1;
            const ltmGlobalForRow =
                subCategoryLtmById.get(String(row.id)) ??
                subcategoryPctOfChannel(categories, row);
            const siblingLtmGlobalTotal =
                siblings.reduce(
                    (sum, item) =>
                        sum +
                        (subCategoryLtmById.get(String(item.id)) ??
                            subcategoryPctOfChannel(categories, item)),
                    0
                ) || 1;
            const actualWeight =
                siblingActuals > 0
                    ? (row.ytd_actuals ?? 0) / siblingActuals
                    : currentPct / Math.max(siblingWithinCategoryTotal, 0.0001);
            const historicalShareWithinCategory = ltmGlobalForRow / siblingLtmGlobalTotal;
            const desiredPct = clamp(
                actualWeight * 0.7 + historicalShareWithinCategory * 0.3,
                0,
                1
            );
            const ytdForecast = computeSubcategoryYtdForecast({
                ytdForecastTotal,
                categories,
                subCategoryRow: row,
            });
            const variancePct =
                ytdForecast > 0 ? ((row.ytd_actuals ?? 0) - ytdForecast) / ytdForecast : 0;
            const recommendedPct = cappedRecommendation(
                currentPct,
                desiredPct,
                variancePct,
                closedMonths
            );
            const pctDelta = recommendedPct - currentPct;
            const unitsToOrder = tableRecords
                .filter((record) => record.subCategory === row.sub_category)
                .reduce((sum, record) => sum + toUnits(record.unitsToOrder), 0);
            const stance = stanceForVariance(variancePct, closedMonths);

            return {
                recommendationId: `forecast-subcategory:${row.id}`,
                id: row.id,
                subCategory: row.sub_category,
                category: row.category,
                currentPct,
                recommendedPct,
                pctDelta,
                pctOfChannel: subcategoryPctOfChannel(categories, row),
                recommendedPctOfChannel: parentCategoryPct * recommendedPct,
                actualShareWithinCategory: actualWeight,
                historicalShareWithinCategory,
                variancePct,
                ytdForecast,
                ytdActual: row.ytd_actuals ?? 0,
                unitsToOrder,
                stance,
                governanceSummary: governanceSummary(
                    stance,
                    variancePct,
                    closedMonths,
                    row.sub_category
                ),
            };
        })
        .filter((card) => Math.abs(card.pctDelta) >= 0.0005 && card.stance !== "Hold")
        .sort((left, right) => Math.abs(right.variancePct) - Math.abs(left.variancePct))
        .slice(0, 8);

    return {
        topLineRecommendation,
        categoryCards,
        subCategoryCards,
        ytdForecastTotal,
        demandUnitsInScope: tableRecords.reduce(
            (sum, record) => sum + toUnits(record.unitsToOrder),
            0
        ),
        categoryTotal: categories.reduce((sum, row) => sum + (row.pct ?? 0), 0),
        subCategoryTotal: subCategories.reduce(
            (sum, row) => sum + subcategoryPctOfChannel(categories, row),
            0
        ),
    };
}

export function getForecastButtonLabel(isActionable, stance, reviewOnly = false) {
    if (isActionable) return "Approve shift";
    if (reviewOnly) return "Log review";
    if (stance === "Hold") return "Log hold";
    return "Log decision";
}
